import React, { useState, useRef, useCallback, useEffect } from 'react';

interface ImageCropModalProps {
  imageSrc: string;
  onCrop: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

interface DragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({ imageSrc, onCrop, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  const [isReady, setIsReady] = useState(false);

  const CANVAS_SIZE = 300;
  const CIRCLE_RADIUS = 130;

  const clampOffset = useCallback(
    (ox: number, oy: number, s: number, imgW: number, imgH: number) => {
      const scaledW = imgW * s;
      const scaledH = imgH * s;
      const minX = CANVAS_SIZE / 2 - scaledW + CANVAS_SIZE / 2 - CANVAS_SIZE / 2;
      const maxX = CANVAS_SIZE / 2;
      const minY = CANVAS_SIZE / 2 - scaledH + CANVAS_SIZE / 2 - CANVAS_SIZE / 2;
      const maxY = CANVAS_SIZE / 2;

      const halfW = scaledW / 2;
      const halfH = scaledH / 2;
      const cx = CANVAS_SIZE / 2;
      const cy = CANVAS_SIZE / 2;

      const clampedX = Math.min(cx - (cx - CIRCLE_RADIUS) + halfW - halfW, Math.max(cx - halfW + (halfW - CIRCLE_RADIUS), ox));
      const clampedY = Math.min(cy - (cy - CIRCLE_RADIUS) + halfH - halfH, Math.max(cy - halfH + (halfH - CIRCLE_RADIUS), oy));

      const left = ox - halfW;
      const right = ox + halfW;
      const top = oy - halfH;
      const bottom = oy + halfH;

      let nx = ox;
      let ny = oy;

      if (left > cx - CIRCLE_RADIUS) nx = cx - CIRCLE_RADIUS + halfW;
      if (right < cx + CIRCLE_RADIUS) nx = cx + CIRCLE_RADIUS - halfW;
      if (top > cy - CIRCLE_RADIUS) ny = cy - CIRCLE_RADIUS + halfH;
      if (bottom < cy + CIRCLE_RADIUS) ny = cy + CIRCLE_RADIUS - halfH;

      return { x: nx, y: ny };
      void minX; void maxX; void minY; void maxY; void clampedX; void clampedY;
    },
    []
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.save();
    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;
    const scaledW = imgW * scale;
    const scaledH = imgH * scale;
    ctx.drawImage(img, offset.x - scaledW / 2, offset.y - scaledH / 2, scaledW, scaledH);
    ctx.restore();

    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CIRCLE_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = 'rgba(236,72,153,0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CIRCLE_RADIUS, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }, [scale, offset]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      const fitScale = Math.max(
        (CIRCLE_RADIUS * 2) / img.naturalWidth,
        (CIRCLE_RADIUS * 2) / img.naturalHeight
      );
      setScale(fitScale);
      setOffset({ x: CANVAS_SIZE / 2, y: CANVAS_SIZE / 2 });
      setIsReady(true);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  useEffect(() => {
    if (isReady) draw();
  }, [draw, isReady]);

  const getEventPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_SIZE / rect.width;
    const scaleY = CANVAS_SIZE / rect.height;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    const pos = getEventPos(e);
    setDragState({
      isDragging: true,
      startX: pos.x,
      startY: pos.y,
      originX: offset.x,
      originY: offset.y,
    });
  };

  const handlePointerMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!dragState.isDragging || !imageRef.current) return;
      e.preventDefault();
      const pos = getEventPos(e);
      const dx = pos.x - dragState.startX;
      const dy = pos.y - dragState.startY;
      const newX = dragState.originX + dx;
      const newY = dragState.originY + dy;
      const clamped = clampOffset(newX, newY, scale, imageRef.current.naturalWidth, imageRef.current.naturalHeight);
      setOffset(clamped);
    },
    [dragState, scale, clampOffset]
  );

  const handlePointerUp = () => {
    setDragState((prev) => ({ ...prev, isDragging: false }));
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (!imageRef.current) return;
    const delta = e.deltaY < 0 ? 0.05 : -0.05;
    const img = imageRef.current;
    const minScale = Math.max(
      (CIRCLE_RADIUS * 2) / img.naturalWidth,
      (CIRCLE_RADIUS * 2) / img.naturalHeight
    );
    const newScale = Math.min(5, Math.max(minScale, scale + delta));
    const clamped = clampOffset(offset.x, offset.y, newScale, img.naturalWidth, img.naturalHeight);
    setScale(newScale);
    setOffset(clamped);
  };

  const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!imageRef.current) return;
    const img = imageRef.current;
    const minScale = Math.max(
      (CIRCLE_RADIUS * 2) / img.naturalWidth,
      (CIRCLE_RADIUS * 2) / img.naturalHeight
    );
    const newScale = Math.min(5, Math.max(minScale, parseFloat(e.target.value)));
    const clamped = clampOffset(offset.x, offset.y, newScale, img.naturalWidth, img.naturalHeight);
    setScale(newScale);
    setOffset(clamped);
  };

  const handleCrop = () => {
    const img = imageRef.current;
    if (!img) return;

    const outputCanvas = document.createElement('canvas');
    const outputSize = 400;
    outputCanvas.width = outputSize;
    outputCanvas.height = outputSize;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.clip();

    const scaledW = img.naturalWidth * scale;
    const scaledH = img.naturalHeight * scale;
    const imgLeft = offset.x - scaledW / 2;
    const imgTop = offset.y - scaledH / 2;

    const cropLeft = CANVAS_SIZE / 2 - CIRCLE_RADIUS;
    const cropTop = CANVAS_SIZE / 2 - CIRCLE_RADIUS;
    const cropSize = CIRCLE_RADIUS * 2;

    const srcX = ((cropLeft - imgLeft) / scaledW) * img.naturalWidth;
    const srcY = ((cropTop - imgTop) / scaledH) * img.naturalHeight;
    const srcW = (cropSize / scaledW) * img.naturalWidth;
    const srcH = (cropSize / scaledH) * img.naturalHeight;

    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputSize, outputSize);

    outputCanvas.toBlob((blob) => {
      if (blob) onCrop(blob);
    }, 'image/jpeg', 0.92);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[#1a1a1a] rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-white/10">
        <h2 className="text-white font-bold text-lg mb-1 text-center">Crop Profile Photo</h2>
        <p className="text-gray-400 text-xs text-center mb-4">Drag and scroll to adjust your photo</p>

        <div
          ref={containerRef}
          className="relative mx-auto mb-4"
          style={{ width: CANVAS_SIZE, height: CANVAS_SIZE, maxWidth: '100%' }}
        >
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="rounded-xl w-full h-full"
            style={{ cursor: dragState.isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
            onWheel={handleWheel}
          />
        </div>

        <div className="mb-5 px-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-gray-400 text-xs">Zoom</span>
            <span className="text-gray-400 text-xs">{Math.round(scale * 100)}%</span>
          </div>
          <input
            type="range"
            min={imageRef.current
              ? Math.max((CIRCLE_RADIUS * 2) / imageRef.current.naturalWidth, (CIRCLE_RADIUS * 2) / imageRef.current.naturalHeight)
              : 0.1}
            max={5}
            step={0.01}
            value={scale}
            onChange={handleScaleChange}
            className="w-full accent-white"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-lg bg-[#2a2a2a] hover:bg-[#333] text-white/70 hover:text-white text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCrop}
            className="flex-1 py-2.5 rounded-lg bg-white hover:bg-gray-200 text-black text-sm font-medium transition-colors"
          >
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
};
